import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import PrintOrderModal from '../components/PrintOrderModal';
import CustomLabelModal from '../components/CustomLabelModal';
import LostCustomersModal from '../components/LostCustomersModal';
import OrderSearchModal from '../components/OrderSearchModal';
import { UpcomingDeliveryCard, SLOT_LABELS } from '../components/SubscriptionDeliveryCard';
import {
  ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, BarChart, Bar,
} from 'recharts';
import moment from 'moment';
import '../farmDashboard.css';
import { stagger, onEnter, onLeave } from '../lib/stagger';
import { Input, ModalShell, Button, PageHeader, Stat, StatRow } from '../lib/ui';
import { isSubOrder } from '../lib/lostCustomers';
import { duePrintOrders } from '../lib/subscriptionPrintOrders';
import { fillMissingDays, withRevenueSplit } from '../lib/salesRevenue';
import { api } from '../lib/auth';

const API_BASE     = 'https://teardrophydro.co.uk/garageFarm/orders';
const SUB_API_BASE = 'https://teardrophydro.co.uk/garageFarm/subscriptions';
const SHORT_SLOT_LABELS = { morning: 'Morning', evening: 'Evening', late: 'Late' };

// ── Traceability content (shared between table row and mobile card) ───────────
const TraceabilityContent = ({ order, onSave }) => {
  const buildInitial = () =>
    (order.items || [])
      .filter(item => item.name !== 'Local delivery')
      .map(item => {
        const existing = (order.traceability || []).find(t => t.itemName === item.name) || {};
        return {
          itemName: item.name,
          qty:      item.quantity,
          tray:     existing.tray  || '',
          bench:    existing.bench || '',
          notes:    existing.notes || '',
        };
      });

  const [rows, setRows]     = useState(buildInitial);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved]   = useState(false);

  const update = (idx, field, value) =>
    setRows(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('accessToken');
      await axios.patch(`${API_BASE}/${order._id}/traceability`,
        { traceability: rows.map(({ qty, ...rest }) => rest) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSaved(true);
      onSave(order._id, rows);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  const addr = order.address;
  const hasAddress = addr?.line1;
  const hasInstructions = !!order.deliveryInstructions;
  const hasSlot = !!order.deliverySlot;

  return (
    <div style={{ background: 'var(--panel-2)', padding: '12px 16px', borderRadius: 'var(--r-xl)' }}>

        {/* ── Delivery details ─────────────────────────────────────────── */}
        {(hasAddress || hasInstructions || hasSlot || order.phone) && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: hasAddress && (hasInstructions || hasSlot || order.phone) ? 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))' : '1fr',
            gap: 12,
            marginBottom: 14,
            padding: '10px 12px',
            background: '#fff',
            border: '1px solid var(--border)',
            borderRadius: 'var(--r-lg)',
            fontSize: 12,
          }}>
            {hasAddress && (
              <div>
                <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--muted)', display: 'block', marginBottom: 4 }}>
                  Deliver to
                </span>
                <div style={{ lineHeight: 1.7, color: 'var(--text)' }}>
                  {order.name && <div style={{ fontWeight: 600 }}>{order.name}</div>}
                  <div>{addr.line1}</div>
                  {addr.line2 && <div>{addr.line2}</div>}
                  <div>{addr.city}{addr.postcode ? `, ${addr.postcode}` : ''}</div>
                  {order.phone && (
                    <a href={`tel:${order.phone}`} style={{ color: 'var(--brand-2)', fontWeight: 600, textDecoration: 'none', display: 'block', marginTop: 3 }}>
                      📞 {order.phone}
                    </a>
                  )}
                </div>
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {!hasAddress && order.phone && (
                <div>
                  <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--muted)', display: 'block', marginBottom: 4 }}>
                    Phone
                  </span>
                  <a href={`tel:${order.phone}`} style={{ color: 'var(--brand-2)', fontWeight: 600, textDecoration: 'none' }}>
                    📞 {order.phone}
                  </a>
                </div>
              )}
              {hasInstructions && (
                <div>
                  <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--muted)', display: 'block', marginBottom: 4 }}>
                    Delivery instructions
                  </span>
                  <div style={{ color: 'var(--text)', fontStyle: 'italic', lineHeight: 1.5 }}>
                    "{order.deliveryInstructions}"
                  </div>
                </div>
              )}
              {order.deliverySlot && (
                <div>
                  <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--muted)', display: 'block', marginBottom: 4 }}>
                    Requested window
                  </span>
                  <div style={{ color: 'var(--text)' }}>
                    🕐 {SLOT_LABELS[order.deliverySlot] || order.deliverySlot}
                    {order.lateNote && <div style={{ fontStyle: 'italic', color: 'var(--muted)', marginTop: 2 }}>"{order.lateNote}"</div>}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Traceability table ───────────────────────────────────────── */}
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
          <thead>
            <tr style={{ color: 'var(--muted)' }}>
              <th style={{ textAlign: 'left', paddingBottom: 4, width: '30%' }}>Item</th>
              <th style={{ textAlign: 'left', paddingBottom: 4, width: '5%'  }}>Qty</th>
              <th style={{ textAlign: 'left', paddingBottom: 4, width: '18%' }}>Tray</th>
              <th style={{ textAlign: 'left', paddingBottom: 4, width: '18%' }}>Bench</th>
              <th style={{ textAlign: 'left', paddingBottom: 4             }}>Notes</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => (
              <tr key={row.itemName}>
                <td style={{ padding: '3px 6px 3px 0' }}>{row.itemName}</td>
                <td style={{ padding: '3px 6px 3px 0', fontWeight: 600 }}>{row.qty}</td>
                {['tray', 'bench', 'notes'].map(field => (
                  <td key={field} style={{ padding: '3px 6px 3px 0' }}>
                    <input
                      value={row[field]}
                      onChange={e => update(idx, field, e.target.value)}
                      placeholder={field.charAt(0).toUpperCase() + field.slice(1)}
                      style={{
                        width: '100%', border: '1px solid var(--border)', borderRadius: 'var(--r-md)',
                        padding: '3px 6px', fontSize: '12px', background: '#fff',
                      }}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ marginTop: 8, textAlign: 'right' }}>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              background: saved ? '#22c55e' : 'var(--brand-2)', color: '#fff',
              border: 'none', borderRadius: 'var(--r-md)', padding: '5px 14px',
              fontSize: '12px', cursor: 'pointer',
            }}
          >
            {saved ? '✓ Saved' : saving ? 'Saving…' : 'Save'}
          </button>
        </div>
    </div>
  );
};

const TraceabilityRow = ({ order, onSave }) => (
  <tr>
    <td colSpan={9} style={{ padding: 0 }}>
      <TraceabilityContent order={order} onSave={onSave} />
    </td>
  </tr>
);

// ── Add Order Modal ───────────────────────────────────────────────────────────
const EMPTY_ITEM = () => ({ name: '', quantity: 1, priceEach: '' });

const AddOrderModal = ({ onClose, onCreated }) => {
  const [name,         setName]         = useState('');
  const [email,        setEmail]        = useState('');
  const [phone,        setPhone]        = useState('');
  const [addrLine1,    setAddrLine1]    = useState('');
  const [addrLine2,    setAddrLine2]    = useState('');
  const [addrCity,     setAddrCity]     = useState('');
  const [addrPostcode, setAddrPostcode] = useState('');
  const [instructions, setInstructions] = useState('');
  const [items,        setItems]        = useState([EMPTY_ITEM()]);
  const [saving,       setSaving]       = useState(false);
  const [error,        setError]        = useState('');

  const total = items.reduce((sum, it) => {
    const p = parseFloat(it.priceEach) || 0;
    const q = parseInt(it.quantity,  10) || 0;
    return sum + p * q;
  }, 0);

  const updateItem = (idx, field, value) =>
    setItems(prev => prev.map((it, i) => i === idx ? { ...it, [field]: value } : it));

  const addItem    = () => setItems(prev => [...prev, EMPTY_ITEM()]);
  const removeItem = (idx) => setItems(prev => prev.filter((_, i) => i !== idx));

  const handleSubmit = async () => {
    setError('');
    if (!name.trim())                      return setError('Customer name is required.');
    if (items.some(it => !it.name.trim())) return setError('Every item must have a name.');
    if (items.some(it => !(parseFloat(it.priceEach) >= 0))) return setError('Every item needs a valid price.');

    setSaving(true);
    try {
      const token = localStorage.getItem('accessToken');
      const payload = {
        name:  name.trim(),
        email: email.trim(),
        phone: phone.trim(),
        address: addrLine1.trim() ? {
          line1:    addrLine1.trim(),
          line2:    addrLine2.trim() || null,
          city:     addrCity.trim(),
          postcode: addrPostcode.trim().toUpperCase(),
          country:  'GB',
        } : null,
        deliveryInstructions: instructions.trim() || '',
        items: items.map(it => ({
          name:      it.name.trim(),
          quantity:  parseInt(it.quantity, 10) || 1,
          priceEach: parseFloat(it.priceEach) || 0,
        })),
        total,
      };
      const res = await axios.post(API_BASE, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      onCreated(res.data);
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create order.');
    } finally {
      setSaving(false);
    }
  };

  const labelStyle = {
    fontSize: '11px', color: 'var(--muted)', fontWeight: 600,
    textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: 4,
  };
  const sectionLabel = {
    fontSize: '11px', color: 'var(--muted)', fontWeight: 700,
    textTransform: 'uppercase', letterSpacing: '0.06em',
    borderBottom: '1px solid var(--border)', paddingBottom: 6, marginBottom: 12,
  };

  return (
    <ModalShell onClose={onClose} width={540} maxWidth="95vw" ariaLabel="Add Cash Order" panelStyle={{ padding: '28px 28px 24px' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 22 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '17px', fontWeight: 700, color: 'var(--text)' }}>Add Cash Order</h2>
            <p style={{ margin: '3px 0 0', fontSize: '12px', color: 'var(--muted)' }}>
              Manually record an order not processed through Stripe.
            </p>
          </div>
          <button onClick={onClose} style={{
            background: 'var(--panel-2)', border: 'none', borderRadius: 'var(--r-md)',
            width: 30, height: 30, cursor: 'pointer', fontSize: '16px', color: 'var(--muted)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>✕</button>
        </div>

        {/* ── Customer ── */}
        <p style={sectionLabel}>Customer</p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: 12 }}>
          <div>
            <label style={labelStyle}>Full name *</label>
            <Input value={name} onChange={e => setName(e.target.value)} placeholder="Jane Smith" />
          </div>
          <div>
            <label style={labelStyle}>Mobile number</label>
            <Input type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="07700 900000" />
          </div>
        </div>
        <div style={{ marginBottom: 20 }}>
          <label style={labelStyle}>Email</label>
          <Input value={email} onChange={e => setEmail(e.target.value)} placeholder="jane@example.com" />
        </div>

        {/* ── Delivery address ── */}
        <p style={sectionLabel}>Delivery address</p>
        <div style={{ display: 'grid', gap: 10, marginBottom: 12 }}>
          <div>
            <label style={labelStyle}>Address line 1</label>
            <Input value={addrLine1} onChange={e => setAddrLine1(e.target.value)} placeholder="12 Example Street" />
          </div>
          <div>
            <label style={labelStyle}>Address line 2 <span style={{ fontWeight: 400, textTransform: 'none' }}>(optional)</span></label>
            <Input value={addrLine2} onChange={e => setAddrLine2(e.target.value)} placeholder="Flat 3" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 10 }}>
            <div>
              <label style={labelStyle}>Town / city</label>
              <Input value={addrCity} onChange={e => setAddrCity(e.target.value)} placeholder="Hockley" />
            </div>
            <div>
              <label style={labelStyle}>Postcode</label>
              <Input style={{ width: 90, textTransform: 'uppercase' }}
                value={addrPostcode}
                onChange={e => setAddrPostcode(e.target.value.toUpperCase())}
                placeholder="SS5 4AA" />
            </div>
          </div>
          <div>
            <label style={labelStyle}>Delivery instructions <span style={{ fontWeight: 400, textTransform: 'none' }}>(optional)</span></label>
            <Input value={instructions} onChange={e => setInstructions(e.target.value)} placeholder="Leave at side gate…" />
          </div>
        </div>

        {/* ── Items ── */}
        <p style={{ ...sectionLabel, marginTop: 8 }}>Items</p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 56px 80px 28px', gap: 6, marginBottom: 6 }}>
          {['Product', 'Qty', '£ Each', ''].map(h => (
            <span key={h} style={{ fontSize: '11px', color: 'var(--muted)', fontWeight: 600 }}>{h}</span>
          ))}
        </div>

        {items.map((item, idx) => (
          <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr 56px 80px 28px', gap: 6, marginBottom: 6 }}>
            <Input
              value={item.name}
              onChange={e => updateItem(idx, 'name', e.target.value)}
              placeholder="Microgreens – Sunflower"
            />
            <Input
              style={{ textAlign: 'center' }}
              type="number" min="1"
              value={item.quantity}
              onChange={e => updateItem(idx, 'quantity', e.target.value)}
            />
            <Input
              style={{ textAlign: 'right' }}
              type="number" min="0" step="0.01"
              value={item.priceEach}
              onChange={e => updateItem(idx, 'priceEach', e.target.value)}
              placeholder="0.00"
            />
            <button
              onClick={() => removeItem(idx)}
              disabled={items.length === 1}
              style={{
                background: 'none', border: 'none', cursor: items.length === 1 ? 'default' : 'pointer',
                color: items.length === 1 ? 'var(--border)' : '#f87171', fontSize: '16px',
                padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >✕</button>
          </div>
        ))}

        <button
          onClick={addItem}
          style={{
            background: 'none', border: '1px dashed var(--border)', borderRadius: 'var(--r-md)',
            padding: '6px 14px', fontSize: '12px', color: 'var(--muted)', cursor: 'pointer',
            width: '100%', marginBottom: 20,
          }}
        >+ Add item</button>

        {/* Total */}
        <div style={{
          background: 'var(--panel-2)', borderRadius: 'var(--r-lg)', padding: '10px 14px',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          marginBottom: 20,
        }}>
          <span style={{ fontSize: '13px', color: 'var(--muted)' }}>Order total</span>
          <span style={{ fontSize: '18px', fontWeight: 700, color: 'var(--text)' }}>£{total.toFixed(2)}</span>
        </div>

        {/* Error */}
        {error && (
          <p style={{
            background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626',
            borderRadius: 'var(--r-lg)', padding: '8px 12px', fontSize: '12px', marginBottom: 16,
          }}>{error}</p>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            style={{
              background: 'var(--panel-2)', border: 'none', borderRadius: 'var(--r-md)', padding: '8px 18px',
              fontSize: '13px', cursor: 'pointer', color: 'var(--muted)', fontWeight: 500,
            }}
          >Cancel</button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            style={{
              background: saving ? 'var(--accent)' : 'var(--brand)', color: '#fff', border: 'none',
              borderRadius: 'var(--r-md)', padding: '8px 22px', fontSize: '13px',
              cursor: saving ? 'default' : 'pointer', fontWeight: 600,
            }}
          >{saving ? 'Saving…' : 'Create Order'}</button>
        </div>
    </ModalShell>
  );
};

// ── Upcoming subscription deliveries ─────────────────────────────────────────
const UpcomingDeliveries = ({ deliveries, onRefresh }) => {
  if (!deliveries?.length) return null;
  return (
    <div className="chart-container col-span-3" onMouseEnter={onEnter} onMouseLeave={onLeave}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <h3 style={{ margin: 0 }}>Upcoming Subscription Deliveries</h3>
        <span style={{ fontSize: 12, color: 'var(--muted)' }}>Today, overdue &amp; next 7 days - {deliveries.length} due</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
        {deliveries.map(sub => (
          <UpcomingDeliveryCard key={sub._id} sub={sub} onDone={onRefresh} />
        ))}
      </div>
    </div>
  );
};

// ── Subscription deliveries due today (print-only placeholders) ──────────────
const DUE_TAG_STYLE = {
  background: '#f3e8ff', color: '#7c3aed', fontSize: '10px',
  fontWeight: 700, padding: '1px 6px', borderRadius: 'var(--r-sm)',
  letterSpacing: '0.04em', textTransform: 'uppercase',
};

const dueItemSummary = (order) =>
  order.items.map(i => i.quantity > 1 ? `${i.name} ×${i.quantity}` : i.name).join(', ') || '-';

const DueSubCard = ({ order, onPrint }) => (
  <div className="order-card" style={{ borderLeft: '3px dashed #7c3aed', background: '#faf5ff' }}>
    <div className="order-card-header">
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <b style={{ fontSize: 15 }}>{order.name}</b>
        <span style={DUE_TAG_STYLE}>Sub due</span>
      </div>
      <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted)' }}>not charged yet</span>
    </div>
    <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
      {moment(order.timestamp).format('DD MMM YY')}
      {order.deliverySlot && (
        <span style={{ background: 'var(--panel-2)', color: 'var(--text)', fontSize: 11, fontWeight: 600, padding: '1px 6px', borderRadius: 'var(--r-sm)' }}>
          🕐 {SHORT_SLOT_LABELS[order.deliverySlot] || order.deliverySlot}
        </span>
      )}
    </div>
    <div style={{ fontSize: 13, marginBottom: 10, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
      {dueItemSummary(order)}
    </div>
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
      <button
        onClick={() => onPrint(order)}
        style={{
          background: '#f0fdf4', color: '#16a34a',
          border: 'none', borderRadius: 'var(--r-lg)', padding: '5px 16px',
          fontSize: 13, cursor: 'pointer', fontWeight: 600,
        }}
      >Print</button>
      <span style={{ fontSize: 11, color: 'var(--muted)', fontStyle: 'italic' }}>Charge or log it on the Subscribers page</span>
    </div>
  </div>
);

const DueSubRow = ({ order, onPrint }) => (
  <tr style={{ background: '#faf5ff', borderLeft: '3px dashed #7c3aed' }}>
    <td />
    <td style={{ fontSize: '14px' }}>
      <div>{moment(order.timestamp).format('DD MMM YY')}</div>
      {order.deliverySlot && (
        <span style={{ background: 'var(--panel-2)', color: 'var(--text)', fontSize: 11, fontWeight: 600, padding: '1px 6px', borderRadius: 'var(--r-sm)', display: 'inline-block', marginTop: 3 }}>
          🕐 {SHORT_SLOT_LABELS[order.deliverySlot] || order.deliverySlot}
        </span>
      )}
    </td>
    <td>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <b>{order.name}</b>
        <span style={DUE_TAG_STYLE}>Sub due</span>
      </div>
      {order.phone && (
        <a href={`tel:${order.phone}`} style={{ fontSize: '13px', color: 'var(--brand-2)', textDecoration: 'none', fontWeight: 500 }}>
          {order.phone}
        </a>
      )}
    </td>
    <td style={{ maxWidth: 220 }}>
      <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '13px' }}>
        {dueItemSummary(order)}
      </div>
      <div style={{ fontSize: '11px', color: 'var(--muted)', fontStyle: 'italic' }}>charge or log on Subscribers page</div>
    </td>
    <td><span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--muted)' }}>not charged yet</span></td>
    <td />
    <td />
    <td>
      <button
        onClick={() => onPrint(order)}
        style={{
          background: '#f0fdf4', color: '#16a34a', border: 'none',
          borderRadius: 'var(--r-lg)', padding: '3px 12px', fontSize: '13px',
          cursor: 'pointer', fontWeight: 600,
        }}
      >Print</button>
    </td>
    <td />
  </tr>
);

// ── Main dashboard ────────────────────────────────────────────────────────────
// ── Monthly revenue target ────────────────────────────────────────────────────
// Target compounds from a fixed base month's actual revenue, so a missed month
// doesn't drag the later targets down with it. The growth rate is set on the
// page and kept on the server too (Briefings settings), so the business
// briefings measure against the same target; garageFarmServer
// helpers/revenue.js mirrors the sum. The monthly chart always runs
// FUTURE_MONTHS past the current month so next month's target is visible.
const TARGET_BASE_MONTH     = '2026-07';
const DEFAULT_TARGET_GROWTH = 1.5;   // per month
const TARGET_GROWTH_KEY     = 'ordersTargetGrowth';
const FUTURE_MONTHS         = 2;

const readTargetGrowth = () => {
  try {
    const n = Number(localStorage.getItem(TARGET_GROWTH_KEY));
    return n > 0 ? n : DEFAULT_TARGET_GROWTH;
  } catch { return DEFAULT_TARGET_GROWTH; }
};

// Fills month gaps from the first month with data to FUTURE_MONTHS ahead and
// adds a `target` to every month from TARGET_BASE_MONTH on. Future months get
// null actuals so their revenue/order lines stop at today.
const withMonthlyTarget = (rows, growth) => {
  if (!rows.length) return rows;
  const byMonth = Object.fromEntries(rows.map(r => [r._id, r]));
  const base    = byMonth[TARGET_BASE_MONTH]?.revenue || 0;
  const now     = moment().startOf('month');
  const last    = moment(now).add(FUTURE_MONTHS, 'months');
  const out     = [];
  for (let m = moment(rows[0]._id, 'YYYY-MM'); !m.isAfter(last); m.add(1, 'month')) {
    const id  = m.format('YYYY-MM');
    const row = byMonth[id] || (m.isAfter(now)
      ? { _id: id, revenue: null, subscriptionRevenue: null, tradeRevenue: null, oneOffRevenue: null, oneOffCount: null, ordersInclSubs: null }
      : { _id: id, revenue: 0, subscriptionRevenue: 0, tradeRevenue: 0, oneOffRevenue: 0, oneOffCount: 0, ordersInclSubs: 0 });
    const steps = m.diff(moment(TARGET_BASE_MONTH, 'YYYY-MM'), 'months');
    out.push({ ...row, target: base && steps >= 0 ? Math.round(base * growth ** steps * 100) / 100 : null });
  }
  return out;
};

// header: the Sales page's tabs (see SalesPage.jsx), passed on to PageHeader
const OrdersDashboard = ({ header }) => {
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [days, setDays]           = useState(30);
  const [monthlyView, setMonthlyView] = useState(true);
  const [monthlyRevenue, setMonthlyRevenue] = useState([]);
  const [targetGrowth, setTargetGrowth] = useState(readTargetGrowth);
  const growthSave = useRef(null);
  // The server's growth rate wins; a rate only this browser had is sent up.
  // An older server without /business routes just leaves the local one.
  useEffect(() => {
    api.get('/business/settings').then(({ data }) => {
      const server = data.settings?.revenueTargetGrowth;
      if (server > 0) {
        setTargetGrowth(server);
        try { localStorage.setItem(TARGET_GROWTH_KEY, String(server)); } catch {}
      } else if (readTargetGrowth() !== DEFAULT_TARGET_GROWTH) {
        api.patch('/business/settings', { revenueTargetGrowth: readTargetGrowth() }).catch(() => {});
      }
    }).catch(() => {});
  }, []);
  const monthlyChart = useMemo(
    () => withMonthlyTarget(monthlyRevenue, targetGrowth),
    [monthlyRevenue, targetGrowth],
  );
  // Month-to-date pace: straight-line projection of this month's revenue so far
  const monthPace = useMemo(() => {
    const now = moment();
    const row = monthlyChart.find(r => r._id === now.format('YYYY-MM'));
    if (!row) return null;
    const soFar = row.revenue || 0;
    const elapsed = (now.valueOf() - moment(now).startOf('month').valueOf()) / 864e5;
    const days = now.daysInMonth();
    return { month: now.format('MMMM'), soFar, pace: elapsed > 0 ? soFar / elapsed * days : 0, target: row.target, daysLeft: Math.ceil(days - elapsed) };
  }, [monthlyChart]);
  // New vs returning customers, last 12 months with data (future months excluded)
  const customerMonths = useMemo(
    () => monthlyChart.filter(r => r.revenue != null).slice(-12).map(r => ({
      _id: r._id,
      newCustomers: r.newCustomers || 0,
      returningCustomers: r.returningCustomers || 0,
      newCustomerRevenue: r.newCustomerRevenue || 0,
      returningCustomerRevenue: r.returningCustomerRevenue || 0,
    })),
    [monthlyChart],
  );
  const [expanded, setExpanded]   = useState(null);
  const [showAddOrder, setShowAddOrder] = useState(false);
  const [printOrder, setPrintOrder]         = useState(null);
  const [showCustomLabels, setShowCustomLabels] = useState(false);
  const [subAnalytics, setSubAnalytics] = useState(null);
  const [hideSubOrders, setHideSubOrders] = useState(false);
  const [showLostCustomers, setShowLostCustomers] = useState(false);
  const [showSearch, setShowSearch] = useState(false);

  const fetchSubAnalytics = useCallback(async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const res = await axios.get(`${SUB_API_BASE}/analytics`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSubAnalytics(res.data);
    } catch (err) { console.error('Sub analytics fetch failed', err); }
  }, []);

  const fetchMonthlyRevenue = useCallback(async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const res = await axios.get(`${API_BASE}/monthly-revenue`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMonthlyRevenue(withRevenueSplit(res.data.monthly_revenue || []));
    } catch (err) { console.error('Monthly revenue fetch failed', err); }
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      const res = await axios.get(`${API_BASE}?days=${days}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const filled = {
        ...res.data,
        daily_revenue: withRevenueSplit(fillMissingDays(res.data.daily_revenue || [], days)),
      };
      setData(filled);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [days]);

  useEffect(() => { fetchData(); fetchSubAnalytics(); fetchMonthlyRevenue(); }, [fetchData, fetchSubAnalytics, fetchMonthlyRevenue]);

  // "/" opens the customer search from anywhere on the page, unless you're
  // already typing into something.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey) return;
      const tag = e.target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || e.target?.isContentEditable) return;
      e.preventDefault();
      setShowSearch(true);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const toggleFulfilled = async (order) => {
    const next = !order.fulfilled;
    setData(prev => ({
      ...prev,
      recent_orders: prev.recent_orders.map(o =>
        o._id === order._id ? { ...o, fulfilled: next } : o
      ),
    }));
    try {
      const token = localStorage.getItem('accessToken');
      await axios.patch(`${API_BASE}/${order._id}/fulfilled`,
        { fulfilled: next },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (err) {
      console.error(err);
      setData(prev => ({
        ...prev,
        recent_orders: prev.recent_orders.map(o =>
          o._id === order._id ? { ...o, fulfilled: !next } : o
        ),
      }));
    }
  };

  const handleTraceabilitySave = (orderId, rows) => {
    setData(prev => ({
      ...prev,
      recent_orders: prev.recent_orders.map(o =>
        o._id === orderId
          ? { ...o, traceability: rows.map(({ qty, ...rest }) => rest) }
          : o
      ),
    }));
  };

  const handleOrderCreated = (newOrder) => {
    setData(prev => ({
      ...prev,
      recent_orders: [newOrder, ...(prev?.recent_orders || [])],
      summary: prev?.summary
        ? {
            ...prev.summary,
            totalRevenue:  (prev.summary.totalRevenue  || 0) + newOrder.total,
            totalOrders:   (prev.summary.totalOrders   || 0) + 1,
            // avg is over one-off orders only (server excludes subscription orders)
            oneOffRevenue: (prev.summary.oneOffRevenue ?? prev.summary.totalRevenue ?? 0) + newOrder.total,
            oneOffCount:   (prev.summary.oneOffCount   ?? prev.summary.totalOrders  ?? 0) + 1,
            avgOrderValue: ((prev.summary.oneOffRevenue ?? prev.summary.totalRevenue ?? 0) + newOrder.total)
                           / ((prev.summary.oneOffCount ?? prev.summary.totalOrders ?? 0) + 1),
          }
        : prev?.summary,
    }));
  };

  const { summary = {}, daily_revenue = [], popular_items = [], recent_orders = [] } = data || {};

  const nonSubOrders = recent_orders.filter(o => !isSubOrder(o));
  const orderNumMap  = Object.fromEntries(
    nonSubOrders.map((o, i) => [o._id, nonSubOrders.length - i])
  );
  const visibleOrders = hideSubOrders ? nonSubOrders : recent_orders;
  const dueSubOrders  = duePrintOrders(subAnalytics?.upcomingDeliveries);

  if (loading) return (
    <div className="dashboard-container">
      <PageHeader title="Sales" subtitle="Orders, revenue and deliveries" {...header} />
      <div className="loader">Loading Order Data...</div>
    </div>
  );

  return (
    <div className="dashboard-container">
      <PageHeader
        title="Sales"
        subtitle="Orders, revenue and deliveries"
        {...header}
        actions={(
          <>
            <button
              type="button"
              className="search-trigger"
              onClick={() => setShowSearch(true)}
              title="Search every order ever placed (shortcut: /)"
            >
              <span aria-hidden="true" style={{ opacity: 0.6 }}>🔍</span>
              <span style={{ flex: 1, textAlign: 'left' }}>Search customers &amp; orders</span>
              <kbd>/</kbd>
            </button>
            <Button onClick={() => setShowLostCustomers(true)} title="Customers who bought and haven't come back">Lost customers</Button>
            <Button onClick={() => setShowCustomLabels(true)}>Custom labels</Button>
            <Button variant="dark" onClick={() => setShowAddOrder(true)}>+ Cash order</Button>
          </>
        )}
      />

      <div className="utility-strip">
        <div className="orders-actions">
          <div className="button-group">
            {[7, 30, 365].map((v) => (
              <button key={v} onClick={() => { setDays(v); setMonthlyView(false); }} className={!monthlyView && days === v ? 'active' : ''}>{v}d</button>
            ))}
            <button onClick={() => setMonthlyView(true)} className={monthlyView ? 'active' : ''}>Monthly</button>
          </div>
          {monthlyView && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--muted)' }}
              title={`Monthly revenue target, compounding from ${moment(TARGET_BASE_MONTH, 'YYYY-MM').format('MMMM YYYY')}`}>
              Target
              <Input
                type="number"
                min="1"
                step="0.05"
                value={targetGrowth}
                onChange={e => {
                  const n = Number(e.target.value);
                  if (!(n > 0)) return;
                  setTargetGrowth(n);
                  try { localStorage.setItem(TARGET_GROWTH_KEY, String(n)); } catch {}
                  clearTimeout(growthSave.current);
                  growthSave.current = setTimeout(() => {
                    api.patch('/business/settings', { revenueTargetGrowth: n }).catch(() => {});
                  }, 800);
                }}
                style={{ width: 70 }}
              />
            </label>
          )}
        </div>
      </div>

      <StatRow>
        <Stat
          label="Revenue"
          value={`£${(summary.totalRevenue || 0).toFixed(2)}`}
          sub={summary.tradeRevenue > 0 ? `Last ${days} days, £${summary.tradeRevenue.toFixed(2)} from trade` : `Last ${days} days`}
          accent="var(--brand-2)"
        />
        <Stat label="Avg order" value={`£${(summary.avgOrderValue || 0).toFixed(2)}`} sub="One-off orders" />
        {subAnalytics && (
          <>
            <Stat label="Active subscribers" value={subAnalytics.active} accent="var(--accent)" />
            <Stat label="MRR" value={`£${(subAnalytics.mrr || 0).toFixed(2)}`} sub="Monthly recurring" accent="var(--accent)" />
          </>
        )}
      </StatRow>

      <div className="grid-container orders-grid" style={stagger(2)}>

    {/* Revenue Trend */}
<div className="chart-container col-span-3" onMouseEnter={onEnter} onMouseLeave={onLeave}>
  {monthPace && (() => {
    const { month, soFar, pace, target, daysLeft } = monthPace;
    const onTrack = target == null || pace >= target;
    const tone = onTrack ? '#059669' : '#d97706';
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: '4px 18px', fontSize: 13, marginBottom: 10 }}>
        <span><span className="dim">{month} so far:</span> <b>£{soFar.toFixed(2)}</b></span>
        <span><span className="dim">On pace for:</span> <b style={{ color: tone }}>£{pace.toFixed(2)}</b></span>
        {target != null && (
          <>
            <span><span className="dim">Target:</span> <b>£{target.toFixed(2)}</b></span>
            <span style={{ color: tone, fontWeight: 600 }}>
              {onTrack
                ? `On track, £${(pace - target).toFixed(2)} ahead`
                : `£${(target - soFar).toFixed(2)} to go in ${daysLeft} day${daysLeft === 1 ? '' : 's'}`}
            </span>
          </>
        )}
      </div>
    );
  })()}
  <ResponsiveContainer height={350} width="100%">
    <ComposedChart data={monthlyView ? monthlyChart : daily_revenue}>
      <defs>
        <linearGradient id="colorRev" x1="0" y1="0" x2="0" y2="1">
          <stop offset="5%"  stopColor="#10b981" stopOpacity={0.15} />
          <stop offset="95%" stopColor="#10b981" stopOpacity={0}    />
        </linearGradient>
        <linearGradient id="colorSub" x1="0" y1="0" x2="0" y2="1">
          <stop offset="5%"  stopColor="#7c3aed" stopOpacity={0.25} />
          <stop offset="95%" stopColor="#7c3aed" stopOpacity={0}    />
        </linearGradient>
        <linearGradient id="colorTrade" x1="0" y1="0" x2="0" y2="1">
          <stop offset="5%"  stopColor="#db2777" stopOpacity={0.22} />
          <stop offset="95%" stopColor="#db2777" stopOpacity={0}    />
        </linearGradient>
        <linearGradient id="colorOrders" x1="0" y1="0" x2="0" y2="1">
          <stop offset="5%"  stopColor="#4f7ea3" stopOpacity={0.15} />
          <stop offset="95%" stopColor="#4f7ea3" stopOpacity={0}    />
        </linearGradient>
      </defs>
      <CartesianGrid strokeDasharray="3 3" vertical={false} />
      <XAxis
        dataKey="_id"
        tickFormatter={t => moment(t, monthlyView ? 'YYYY-MM' : 'YYYY-MM-DD').format(monthlyView ? 'MMM YY' : 'MMM DD')}
        interval={monthlyView ? 0 : (days <= 30 ? 5 : Math.max(1, Math.floor(days / 10)))}
      />
      <YAxis
        yAxisId="left"
        tickFormatter={val => `£${val}`}
        tick={{ fontSize: 11 }}
      />
      <YAxis
        yAxisId="right"
        orientation="right"
        allowDecimals={false}
        tickFormatter={val => Math.floor(val)}
        tick={{ fontSize: 11 }}
        width={30}
      />
      <Tooltip
        formatter={(value, name) =>
          value == null ? ['-', name]
          : name === 'Orders'
            ? [Math.floor(value), name]
            : [`£${Number(value).toFixed(2)}`, name]
        }
        labelFormatter={label => moment(label, monthlyView ? 'YYYY-MM' : 'YYYY-MM-DD').format(monthlyView ? 'MMMM YYYY' : 'ddd D MMM')}
      />
      <Legend wrapperStyle={{ fontSize: 12 }} />
      <Area
        yAxisId="left"
        name="One-off revenue"
        type="monotone"
        dataKey="oneOffRevenue"
        stackId="revenue"
        stroke="#10b981"
        fill="url(#colorRev)"
        strokeWidth={2}
      />
      <Area
        yAxisId="left"
        name="Subscription revenue"
        type="monotone"
        dataKey="subscriptionRevenue"
        stackId="revenue"
        stroke="#7c3aed"
        fill="url(#colorSub)"
        strokeWidth={2}
      />
      {/* Wholesale invoices (Trade tab), counted on their delivery days */}
      <Area
        yAxisId="left"
        name="Trade revenue"
        type="monotone"
        dataKey="tradeRevenue"
        stackId="revenue"
        stroke="#db2777"
        fill="url(#colorTrade)"
        strokeWidth={2}
      />
      <Line
        yAxisId="left"
        name="Total revenue"
        type="monotone"
        dataKey="revenue"
        stroke="#1f2937"
        strokeWidth={2}
        dot={false}
        activeDot={{ r: 5 }}
      />
      <Area
        yAxisId="right"
        name="Orders"
        type="monotone"
        dataKey="ordersInclSubs"
        stroke="#4f7ea3"
        fill="url(#colorOrders)"
        strokeWidth={2}
        strokeDasharray="4 4"
      />
      {monthlyView && (
        <Line
          yAxisId="left"
          name={`Target (${targetGrowth}× / month)`}
          type="linear"
          dataKey="target"
          stroke="#f59e0b"
          strokeWidth={2}
          strokeDasharray="6 4"
          dot={{ r: 3 }}
          connectNulls={false}
        />
      )}
    </ComposedChart>
  </ResponsiveContainer>
</div>

        <UpcomingDeliveries
          deliveries={subAnalytics?.upcomingDeliveries}
          onRefresh={fetchSubAnalytics}
        />

        {/* Orders Table */}
        <div className="chart-container col-span-2" onMouseEnter={onEnter} onMouseLeave={onLeave}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
            <h3 style={{ margin: 0 }}>Orders</h3>
            <div className="button-group">
              <button className={!hideSubOrders ? 'active' : ''} onClick={() => setHideSubOrders(false)}>
                All ({recent_orders.length})
              </button>
              <button
                className={hideSubOrders ? 'active' : ''}
                onClick={() => setHideSubOrders(true)}
                title="Hide subscription signups, logged deliveries and subscription charges"
              >
                Orders only ({nonSubOrders.length})
              </button>
            </div>
          </div>

          {/* Mobile card view */}
          <div className="orders-mobile-cards">
            {dueSubOrders.map(order => (
              <DueSubCard key={`due-${order._id}`} order={order} onPrint={setPrintOrder} />
            ))}
            {visibleOrders.map((order) => {
              const isOpen   = expanded === order._id;
              const hasTrace = (order.traceability || []).some(t => t.tray || t.bench || t.notes);
              const isSubscription   = isSubOrder(order);
              const isLoggedDelivery = order.subscriptionEvent === 'delivery'
                || (isSubscription && order.total === 0 && !!order.subscriptionPlanId);
              const isSignup = !isLoggedDelivery && order.total === 0 && !order.subscriptionPlanId;
              const isCash   = !isSubscription && (order.paymentMethod === 'cash' || !order.stripeSessionId);
              const isMonthBill = order.revenueAmount === 0 && order.total > 0;
              const hasDeliveryDetail = order.address?.line1 || order.phone || order.deliveryInstructions || order.deliverySlot;
              const itemSummary = order.items
                .filter(i => i.name !== 'Local delivery')
                .map(i => i.quantity > 1 ? `${i.name} ×${i.quantity}` : i.name)
                .join(', ') || '-';
              const hasDelivery = order.items.some(i => i.name === 'Local delivery');
              return (
                <div key={order._id} className="order-card" style={isSubscription ? { borderLeft: '3px solid #7c3aed', background: '#faf5ff' } : {}}>
                  <div className="order-card-header">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      {!isSubscription && (
                        <span style={{ fontSize: 12, color: 'var(--muted)', fontWeight: 600 }}>#{orderNumMap[order._id]}</span>
                      )}
                      <b style={{ fontSize: 15 }}>{order.name}</b>
                      {isSubscription ? (
                        <span style={{
                          background: '#f3e8ff', color: '#7c3aed', fontSize: '10px',
                          fontWeight: 700, padding: '1px 6px', borderRadius: 'var(--r-sm)',
                          letterSpacing: '0.04em', textTransform: 'uppercase',
                        }}>Subscription</span>
                      ) : isCash && (
                        <span style={{
                          background: '#fef9c3', color: '#854d0e', fontSize: '10px',
                          fontWeight: 700, padding: '1px 6px', borderRadius: 'var(--r-sm)',
                          letterSpacing: '0.04em', textTransform: 'uppercase',
                        }}>Cash</span>
                      )}
                    </div>
                    {isSignup
                      ? <span style={{ fontSize: 13, fontWeight: 700, color: '#7c3aed' }}>New member</span>
                      : isLoggedDelivery
                      ? (order.revenueAmount != null
                          ? <b style={{ fontSize: 16 }}>£{order.revenueAmount.toFixed(2)}</b>
                          : <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted)' }}>billed monthly</span>)
                      : <b style={{ fontSize: 16 }}>£{order.total.toFixed(2)}</b>
                    }
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                    {moment(order.timestamp).format('DD MMM YY, HH:mm')}
                    {order.deliverySlot && (
                      <span style={{ background: 'var(--panel-2)', color: 'var(--text)', fontSize: 11, fontWeight: 600, padding: '1px 6px', borderRadius: 'var(--r-sm)' }}>
                        🕐 {SHORT_SLOT_LABELS[order.deliverySlot] || order.deliverySlot}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 13, marginBottom: 10, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {isSignup
                      ? <span style={{ color: '#7c3aed', fontStyle: 'italic' }}>Subscription signup</span>
                      : <>{itemSummary}{hasDelivery && <span style={{ fontSize: 11, color: 'var(--muted)', marginLeft: 4 }}>+ delivery</span>}{isLoggedDelivery && <span style={{ fontSize: 11, color: 'var(--muted)', marginLeft: 4, fontStyle: 'italic' }}>· logged delivery, billed monthly</span>}{isMonthBill && <span style={{ fontSize: 11, color: 'var(--muted)', marginLeft: 4, fontStyle: 'italic' }}>· monthly bill, revenue counted on delivery days</span>}</>
                    }
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button
                      onClick={() => toggleFulfilled(order)}
                      style={{
                        background: order.fulfilled ? '#10b981' : 'var(--panel-2)',
                        color: order.fulfilled ? '#fff' : 'var(--muted)',
                        border: 'none', borderRadius: 'var(--r-lg)', padding: '5px 16px',
                        fontSize: 13, cursor: 'pointer', fontWeight: 600,
                      }}
                    >{order.fulfilled ? 'Fulfilled' : 'Unfulfilled'}</button>
                    <button
                      onClick={() => setExpanded(isOpen ? null : order._id)}
                      style={{
                        background: hasTrace ? 'var(--panel-2)' : hasDeliveryDetail ? '#f0fdf4' : 'var(--panel-2)',
                        color: hasTrace ? 'var(--brand-2)' : hasDeliveryDetail ? '#16a34a' : 'var(--muted)',
                        border: 'none', borderRadius: 'var(--r-lg)', padding: '5px 16px',
                        fontSize: 13, cursor: 'pointer', fontWeight: 600,
                      }}
                    >{isOpen ? 'Close' : hasTrace ? 'Traceability' : hasDeliveryDetail ? 'Delivery' : 'Add trace'}</button>
                    <button
                      onClick={() => setPrintOrder(order)}
                      style={{
                        background: '#f0fdf4', color: '#16a34a',
                        border: 'none', borderRadius: 'var(--r-lg)', padding: '5px 16px',
                        fontSize: 13, cursor: 'pointer', fontWeight: 600,
                      }}
                    >Print</button>
                    {!isCash && (
                      <a
                        href={`https://dashboard.stripe.com/payments/${order.stripeSessionId}`}
                        target="_blank" rel="noreferrer"
                        style={{
                          background: 'var(--panel-2)', color: 'var(--muted)',
                          textDecoration: 'none', padding: '5px 16px',
                          borderRadius: 'var(--r-lg)', fontSize: 13, fontWeight: 500,
                        }}
                      >Stripe</a>
                    )}
                  </div>
                  {isOpen && (
                    <div style={{ marginTop: 10 }}>
                      <TraceabilityContent order={order} onSave={handleTraceabilitySave} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Desktop table view */}
          <div className="custom-table-wrapper orders-desktop-table">
            <table className="stats-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Date</th>
                  <th>Customer</th>
                  <th>Items</th>
                  <th align="right">Total</th>
                  <th align="center">Fulfilled</th>
                  <th align="center">Trace</th>
                  <th align="center">Print</th>
                  <th align="right">Stripe</th>
                </tr>
              </thead>
              <tbody>
                {dueSubOrders.map(order => (
                  <DueSubRow key={`due-${order._id}`} order={order} onPrint={setPrintOrder} />
                ))}
                {visibleOrders.map((order) => {
                  const isOpen   = expanded === order._id;
                  const hasTrace = (order.traceability || []).some(t => t.tray || t.bench || t.notes);
                  const isSubscription   = isSubOrder(order);
                  const isLoggedDelivery = order.subscriptionEvent === 'delivery'
                    || (isSubscription && order.total === 0 && !!order.subscriptionPlanId);
                  const isSignup = !isLoggedDelivery && order.total === 0 && !order.subscriptionPlanId;
                  const isCash   = !isSubscription && (order.paymentMethod === 'cash' || !order.stripeSessionId);
                  const isMonthBill = order.revenueAmount === 0 && order.total > 0;
                  const hasDeliveryDetail = order.address?.line1 || order.phone || order.deliveryInstructions || order.deliverySlot;
                  return (
                    <React.Fragment key={order._id}>
                      <tr style={isSubscription ? { background: '#faf5ff' } : {}}>
                        <td style={{ fontSize: '13px', color: 'var(--muted)', fontWeight: 600 }}>
                          {orderNumMap[order._id] ? `#${orderNumMap[order._id]}` : ''}
                        </td>
                        <td style={{ fontSize: '14px' }}>
                          <div>{moment(order.timestamp).format('DD MMM YY, HH:mm')}</div>
                          {order.deliverySlot && (
                            <span style={{ background: 'var(--panel-2)', color: 'var(--text)', fontSize: 11, fontWeight: 600, padding: '1px 6px', borderRadius: 'var(--r-sm)', display: 'inline-block', marginTop: 3 }}>
                              🕐 {SHORT_SLOT_LABELS[order.deliverySlot] || order.deliverySlot}
                            </span>
                          )}
                        </td>

                        {/* ── Customer cell ── */}
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <b>{order.name}</b>
                            {isSubscription ? (
                              <span style={{
                                background: '#f3e8ff', color: '#7c3aed', fontSize: '10px',
                                fontWeight: 700, padding: '1px 6px', borderRadius: 'var(--r-sm)',
                                letterSpacing: '0.04em', textTransform: 'uppercase',
                              }}>Subscription</span>
                            ) : isCash && (
                              <span style={{
                                background: '#fef9c3', color: '#854d0e', fontSize: '10px',
                                fontWeight: 700, padding: '1px 6px', borderRadius: 'var(--r-sm)',
                                letterSpacing: '0.04em', textTransform: 'uppercase',
                              }}>Cash</span>
                            )}
                          </div>
                          {order.email && (
                            <div className="dim" style={{ fontSize: '13px' }}>{order.email}</div>
                          )}
                          {order.phone && (
                            <a
                              href={`tel:${order.phone}`}
                              style={{ fontSize: '13px', color: 'var(--brand-2)', textDecoration: 'none', fontWeight: 500 }}
                            >
                              {order.phone}
                            </a>
                          )}
                        </td>

                        <td style={{ maxWidth: 220 }}>
                          {isSignup ? (
                            <div style={{ fontSize: '13px', color: '#7c3aed', fontStyle: 'italic' }}>Subscription signup</div>
                          ) : (
                            <>
                              <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '13px' }}>
                                {order.items
                                  .filter(i => i.name !== 'Local delivery')
                                  .map(i => i.quantity > 1 ? `${i.name} ×${i.quantity}` : i.name)
                                  .join(', ') || '-'}
                              </div>
                              {order.items.some(i => i.name === 'Local delivery') && (
                                <span style={{ fontSize: '11px', color: 'var(--muted)' }}>+ delivery</span>
                              )}
                              {isLoggedDelivery && (
                                <div style={{ fontSize: '11px', color: 'var(--muted)', fontStyle: 'italic' }}>logged delivery, billed monthly</div>
                              )}
                              {isMonthBill && (
                                <div style={{ fontSize: '11px', color: 'var(--muted)', fontStyle: 'italic' }}>monthly bill, revenue counted on delivery days</div>
                              )}
                            </>
                          )}
                        </td>
                        <td>
                          {isSignup
                            ? <span style={{ fontSize: '13px', fontWeight: 700, color: '#7c3aed' }}>New member</span>
                            : isLoggedDelivery
                            ? (order.revenueAmount != null
                                ? <b>£{order.revenueAmount.toFixed(2)}</b>
                                : <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--muted)' }}>billed monthly</span>)
                            : <b>£{order.total.toFixed(2)}</b>
                          }
                        </td>

                        <td>
                          <button
                            onClick={() => toggleFulfilled(order)}
                            title="Toggle fulfilled"
                            style={{
                              background:   order.fulfilled ? '#10b981' : 'var(--panel-2)',
                              color:        order.fulfilled ? '#fff'    : 'var(--muted)',
                              border:       'none', borderRadius: 'var(--r-lg)',
                              padding:      '3px 14px', fontSize: '15px',
                              cursor:       'pointer', fontWeight: 600, minWidth: 48,
                            }}
                          >
                            {order.fulfilled ? 'Yes' : 'No'}
                          </button>
                        </td>

                        <td>
                          <button
                            onClick={() => setExpanded(isOpen ? null : order._id)}
                            title={hasDeliveryDetail ? 'View delivery details & traceability' : 'Edit traceability'}
                            style={{
                              background: hasTrace ? 'var(--panel-2)' : hasDeliveryDetail ? '#f0fdf4' : 'var(--panel-2)',
                              color:      hasTrace ? 'var(--brand-2)' : hasDeliveryDetail ? '#16a34a' : 'var(--muted)',
                              border: 'none', borderRadius: 'var(--r-lg)',
                              padding: '3px 10px', fontSize: '15px',
                              cursor: 'pointer', fontWeight: 600,
                            }}
                          >
                            {isOpen ? '▲' : hasTrace ? 'View' : hasDeliveryDetail ? '📍' : 'Add'}
                          </button>
                        </td>

                        <td>
                          <button
                            onClick={() => setPrintOrder(order)}
                            style={{
                              background: '#f0fdf4', color: '#16a34a', border: 'none',
                              borderRadius: 'var(--r-lg)', padding: '3px 12px', fontSize: '13px',
                              cursor: 'pointer', fontWeight: 600,
                            }}
                          >Print</button>
                        </td>

                        <td>
                          {isCash ? (
                            <span style={{
                              background: 'var(--panel-2)', color: 'var(--muted)',
                              padding: '3px 10px', fontSize: '13px',
                              borderRadius: 'var(--r-lg)', display: 'inline-block',
                            }}>-</span>
                          ) : (
                            <a
                              href={`https://dashboard.stripe.com/payments/${order.stripeSessionId}`}
                              target="_blank" rel="noreferrer"
                              className="pill"
                              style={{ background: 'var(--panel-2)', color: 'var(--muted)', textDecoration: 'none', padding: '3px 10px', fontSize: '15px' }}
                            >
                              Stripe
                            </a>
                          )}
                        </td>
                      </tr>

                      {isOpen && (
                        <TraceabilityRow
                          order={order}
                          onSave={handleTraceabilitySave}
                        />
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Right column: Units Sold + New vs Returning */}
        <div className="orders-side">
          <div className="chart-container" onMouseEnter={onEnter} onMouseLeave={onLeave}>
            <h3>Units Sold</h3>
            <ResponsiveContainer height={500} width="100%">
              <BarChart data={popular_items} layout="vertical">
                <XAxis type="number" hide />
                <YAxis dataKey="_id" type="category" width={100} fontSize={11} />
                <Tooltip />
                <Bar dataKey="totalSold" fill="#4f7ea3" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="chart-container" onMouseEnter={onEnter} onMouseLeave={onLeave}>
            <h3>New vs Returning Customers</h3>
            <ResponsiveContainer height={260} width="100%">
              <BarChart data={customerMonths}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="_id" tickFormatter={t => moment(t, 'YYYY-MM').format('MMM')} tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={28} />
                <Tooltip
                  labelFormatter={l => moment(l, 'YYYY-MM').format('MMMM YYYY')}
                  formatter={(value, name, item) => {
                    const rev = name === 'New' ? item.payload.newCustomerRevenue : item.payload.returningCustomerRevenue;
                    return [`${value} (£${Number(rev).toFixed(2)})`, name];
                  }}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="newCustomers" name="New" stackId="c" fill="#10b981" />
                <Bar dataKey="returningCustomers" name="Returning" stackId="c" fill="#4f7ea3" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

      </div>

      {showAddOrder && (
        <AddOrderModal
          onClose={() => setShowAddOrder(false)}
          onCreated={handleOrderCreated}
        />
      )}

      {printOrder && (
        <PrintOrderModal
          order={printOrder}
          onClose={() => setPrintOrder(null)}
          onTraceabilitySaved={handleTraceabilitySave}
        />
      )}

      {showLostCustomers && (
        <LostCustomersModal onClose={() => setShowLostCustomers(false)} />
      )}

      {showSearch && (
        <OrderSearchModal onClose={() => setShowSearch(false)} />
      )}

      {showCustomLabels && (
        <CustomLabelModal onClose={() => setShowCustomLabels(false)} />
      )}
    </div>
  );
};

export default OrdersDashboard;